// Boot only: read URL params, create the session/renderer/sound, wire DOM
// events, start the loop. Everything else lives in session.ts, viewport.ts,
// veils.ts, platform.ts and loop.ts.
import type { World } from '../sim/index.ts';
import { LEVELS, replayOf } from '../sim/index.ts';
import { Renderer } from '../render/canvas.ts';
import { attachPointerInput } from '../input/pointer.ts';
import { nextLevel } from './game.ts';
import type { AppState } from './game.ts';
import type { SoundStats } from './sound.ts';
import { isUnlocked } from './progress.ts';
import { createSession, queryDom } from './session.ts';
import { onResize } from './viewport.ts';
import { keepFlying, quitToMenu, refreshCityChips, refreshSoundToggle, resumeGame, showIntro, startLevel, syncHints, togglePause } from './veils.ts';
import { hideInstallHint, maybeShowInstallHint, onVisibilityChange } from './platform.ts';
import { advance, frame, renderNow } from './loop.ts';

const params = new URLSearchParams(location.search);
const DEV = params.get('dev') === '1';
const urlLevel = params.get('level');
const urlSeedParam = params.get('seed');
const RECORD = params.get('record') === '1';

const dom = queryDom();
const renderer = new Renderer(dom.canvas);
const session = createSession(DEV, RECORD);

function boot(): void {
  refreshCityChips(dom, session);
  refreshSoundToggle(dom, session);
  maybeShowInstallHint(dom);

  window.addEventListener('resize', () => onResize(renderer, session));
  window.addEventListener('orientationchange', () => onResize(renderer, session));
  document.addEventListener('visibilitychange', () => onVisibilityChange(session));
  dom.installHint.addEventListener('pointerup', () => hideInstallHint(dom));

  // iOS: an AudioContext may only be created/resumed inside a user gesture.
  // Capture phase so this always runs first, even on a gesture whose target
  // (e.g. the sound toggle) later calls stopPropagation on the same event.
  const unlockAudio = (): void => session.sound.unlock();
  document.addEventListener('pointerdown', unlockAudio, { capture: true });
  document.addEventListener('touchend', unlockAudio, { capture: true });
  document.addEventListener('click', unlockAudio, { capture: true });

  // Its own small button, not part of #hud/.legend, so it stays visible on
  // the intro veil as well as in play (Q6) — never starts a canvas drag.
  dom.soundToggle.addEventListener('pointerdown', (e) => e.stopPropagation());
  dom.soundToggle.addEventListener('pointerup', (e) => {
    e.stopPropagation();
    session.sound.toggleMuted();
    refreshSoundToggle(dom, session);
  });
  // Pause sits in the same corner and follows the same rules: never a canvas
  // drag, reachable over the veil it raises (where it reads "resume").
  dom.pauseToggle.addEventListener('pointerdown', (e) => e.stopPropagation());
  dom.pauseToggle.addEventListener('pointerup', (e) => {
    e.stopPropagation();
    togglePause(dom, session);
  });
  // Esc on a keyboard (desktop, iPad with a keyboard) — the other way in and out.
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') togglePause(dom, session);
  });

  attachPointerInput(dom.canvas, {
    // only a live run takes commands — while paused the pointer layer sees no
    // world at all, so a gesture still in flight is dropped like a pointercancel
    // rather than landing a route on a frozen sky
    getWorld: () => (session.appState === 'playing' ? session.world : null),
    getViewScale: () => session.vs,
    onDragChange: (id) => { session.dragPlaneId = id; },
    // the finger is an instrument while it draws a route (the sky is a score, src/app/sound.ts)
    onInkPoint: (p, speed) => session.sound.inkMove(p.x, p.y, speed),
    onInkEnd: (result) => session.sound.inkEnd(result),
    // drawing on empty sky commands nothing — it is just ink the renderer fades out
    onDoodleStart: () => renderer.doodleStart(),
    onDoodlePoint: (p) => renderer.doodlePoint(p.x, p.y),
    onCommand: (kind, plane) => {
      if (!session.onboarding) return;
      if (kind === 'path') session.onboarding.notifyPathCommitted();
      else if (plane.holding) session.onboarding.notifyHeld();
      if (session.world) session.onboarding.tick(session.world); // react immediately, don't wait for next fixed step
      syncHints(dom, session);
    },
  });

  // "tap to begin" — but the city list can now be tall enough to scroll on a
  // short viewport, and a scroll ends in a pointerup like a tap does. Only a
  // gesture that stayed put counts as the tap.
  let introDownAt: { x: number; y: number } | null = null;
  dom.intro.addEventListener('pointerdown', (e) => { introDownAt = { x: e.clientX, y: e.clientY }; });
  dom.intro.addEventListener('pointerup', (e) => {
    const from = introDownAt;
    introDownAt = null;
    if (from && Math.hypot(e.clientX - from.x, e.clientY - from.y) > 12) return;
    startLevel(renderer, dom, session, LEVELS[0].id);
  });
  // delegated, not per-row: refreshCityChips() rebuilds the rows' innerHTML
  // (progress can change, a level can unlock) so listeners bound to the old
  // nodes would go stale — one listener on the always-stable container instead
  dom.citiesEl.addEventListener('pointerup', (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>('[data-lvl]');
    if (!row) return;
    e.stopPropagation(); // even when locked — don't fall through to "start the first level"
    const id = row.dataset.lvl ?? '';
    if (!isUnlocked(session.progress, id, DEV)) return;
    startLevel(renderer, dom, session, id);
  });
  // tap the city name in the HUD to hop to the next city — dev testing only
  dom.cityEl.addEventListener('pointerup', (e) => {
    e.stopPropagation();
    if (!DEV || !session.world) return;
    const nxt = nextLevel(LEVELS, session.world.level.id) ?? LEVELS[0];
    startLevel(renderer, dom, session, nxt.id);
  });
  dom.over.addEventListener('pointerup', () => { if (session.world) startLevel(renderer, dom, session, session.world.level.id); });
  dom.clearVeil.addEventListener('pointerup', () => {
    if (!session.world) return;
    const nxt = nextLevel(LEVELS, session.world.level.id);
    startLevel(renderer, dom, session, (nxt ?? LEVELS[0]).id);
  });
  dom.chooseCity.addEventListener('pointerup', (e) => {
    e.stopPropagation(); // don't let clearVeil's own listener also start the next level
    showIntro(renderer, dom, session);
  });
  dom.flyOnEl.addEventListener('pointerup', (e) => {
    e.stopPropagation(); // ...nor start the next city when the ask was to stay on this one
    keepFlying(dom, session);
  });
  dom.pauseVeil.addEventListener('pointerup', () => resumeGame(dom, session));
  dom.toMenu.addEventListener('pointerup', (e) => {
    e.stopPropagation(); // leaving, not resuming
    quitToMenu(renderer, dom, session);
  });

  if (urlLevel) {
    const seed = urlSeedParam !== null && urlSeedParam !== '' ? Number(urlSeedParam) : undefined;
    startLevel(renderer, dom, session, urlLevel, seed !== undefined && Number.isFinite(seed) ? seed : undefined);
  } else {
    showIntro(renderer, dom, session);
  }
  session.last = performance.now();
  requestAnimationFrame((t) => frame(renderer, dom, session, t));
}

// ---------- test/automation hook ----------
declare global {
  interface Window {
    __qs?: {
      readonly world: World | null;
      readonly renderer: Renderer;
      readonly state: AppState;
      /** the sound layer's glance: context state, beats, notes struck, phase, key, levels */
      readonly sound: SoundStats;
      start(levelId: string, seed?: number): void;
      step(n?: number): void;
      /** Test-only: force the collision case deterministically (Q4 e2e) —
       *  overlaps the first two airborne aircraft and steps once, so the sim's
       *  own crash detection (src/sim/world.ts) fires exactly as it would in
       *  play. Returns false (no-op) if fewer than two aircraft are airborne
       *  yet; the caller should wait/poll and retry. */
      crashNow(): boolean;
      /** The current run as Replay JSON for `npm run lab -- --replay` (null unless
       *  the page was opened with ?record=1). Copy it from the console on a phone. */
      replay(): string | null;
    };
  }
}
Object.defineProperty(window, '__qs', {
  configurable: true,
  value: {
    get world() { return session.world; },
    get renderer() { return renderer; },
    get state() { return session.appState; },
    get sound() { return session.sound.stats; },
    start(levelId: string, seed?: number) { startLevel(renderer, dom, session, levelId, seed); },
    replay() {
      const w = session.world;
      return w?.log ? JSON.stringify(replayOf(w, w.level.id, w.W, w.H, w.seed)) : null;
    },
    step(n = 1) {
      advance(renderer, dom, session, n);
      renderNow(renderer, session);
    },
    crashNow() {
      const world = session.world;
      if (!world || world.state !== 'playing' || world.planes.length < 2) return false;
      const [a, b] = world.planes;
      b.x = a.x; b.y = a.y;
      advance(renderer, dom, session, 1);
      renderNow(renderer, session);
      // `session.appState`, not `world.state` — `advance()` above may have
      // flipped `world.state` to 'dead' via `handleStateTransition()`, and
      // this reads the state the rest of the app would see, consistently
      return session.appState === 'dead';
    },
  },
});

boot();
